import {
  GatewayBrowserClient,
  GatewayClientStoppedError,
  GatewayRequestError,
  type GatewayBrowserClientOptions,
} from "./gateway-client";
import {
  RuntimeRequestError,
  RuntimeTransportStoppedError,
  type RuntimeConnectionSnapshot,
  type RuntimeEventFrame,
  type RuntimeTransportLifecycleOptions,
  type RuntimeTransport,
} from "./runtime-transport";

export type GatewayRuntimeTransportOptions = Omit<
  GatewayBrowserClientOptions,
  "onEvent"
> & RuntimeTransportLifecycleOptions & {
  onEvent?: (event: RuntimeEventFrame) => void;
};

function normalizeGatewayError(error: unknown): Error {
  if (error instanceof GatewayClientStoppedError) {
    return new RuntimeTransportStoppedError();
  }
  if (error instanceof GatewayRequestError) {
    return new RuntimeRequestError(
      error.gatewayCode,
      error.message,
      error.details,
      error.gatewayCode,
    );
  }
  return error instanceof Error ? error : new Error(String(error));
}

class GatewayRuntimeTransport implements RuntimeTransport {
  private readonly client: GatewayBrowserClient;

  constructor(options: GatewayRuntimeTransportOptions) {
    this.client = new GatewayBrowserClient({
      ...options,
      onEvent: options.onEvent,
    });
  }

  start(): void {
    this.client.start();
  }

  stop(): void {
    this.client.stop();
  }

  getConnectionSnapshot(): RuntimeConnectionSnapshot {
    return this.client.getConnectionSnapshot();
  }

  async request<T = unknown>(method: string, params?: unknown): Promise<T> {
    try {
      return await this.client.request<T>(method, params);
    } catch (error) {
      throw normalizeGatewayError(error);
    }
  }
}

export function createGatewayRuntimeTransport(
  options: GatewayRuntimeTransportOptions,
): RuntimeTransport {
  return new GatewayRuntimeTransport(options);
}
